import { ApkVariant } from '@/enums/apk'
import { apkVariantLabel, shortFingerprint } from '@/libs/apk'
import { formatDay } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { ApiError } from '@/types/Common'

/** Mirrors the error codes tv-api answers with (docs/API.md → Errors). */
export enum Errors {
    InternalServerError    = 1,
    InvalidData            = 2,
    NotFound               = 3,
    Unauthorized           = 4,
    FailedToInsert         = 5,
    Forbidden              = 6,
    UsernameTaken          = 7,
    LastAdmin              = 8,
    PasswordTooShort       = 9,
    WrongPassword          = 10,
    TooManyAttempts        = 11,
    PasskeysUnavailable    = 12,
    SlugTaken              = 13,
    EnrolmentUnknown       = 14,
    FileTooLarge           = 15,
    UnsupportedMedia       = 16,
    InUse                  = 17,
    TenantInactive         = 18,
    WeatherUnavailable     = 19,
    AlreadyMember          = 20,
    NotAvailable           = 21,
    ConnectorNotConfigured = 22,
    ConnectorFailed        = 23,
    EpgFetchFailed         = 29,
    ChannelAlreadyMapped   = 30,
    VersionExists          = 31,
    ImportInvalid          = 32,
    ApkInvalid             = 33,
    ApkWrongPackage        = 34,
    ApkUnsigned            = 35,
    ApkWrongSigner         = 36,
    VersionTooLow          = 37,
    ThemeKeyTaken          = 38,
    CommandUnavailable     = 41,
    TooManyOpenRequests    = 42,
    RequestStatus          = 43,
    PosFailed              = 44,
    PosNotConfigured       = 45,
    ItemUnavailable        = 46,
    StayOverlap            = 55,
    StayState              = 56,
    StayDates              = 57,
    RoomHasStays           = 58,
    TmsNotConfigured       = 92,
    TmsFailed              = 93,
    NoDeviceMac            = 94,
}

/** The code of an API error body, or 0 when it carries none. */
export const errorCode = (errorObject: unknown): number => {
    const error = (errorObject as ApiError)?.error

    return typeof error === 'object' && error ? error.code ?? 0 : 0
}

/** The `details` of an API error body ({} when it carries none). */
export const errorDetails = (errorObject: unknown): Record<string, unknown> => {
    const error = (errorObject as ApiError)?.error

    return typeof error === 'object' && error?.details ? error.details : {}
}

/** The server's own message of an API error body, trimmed. */
export const errorMessage = (errorObject: unknown): string => {
    const error = (errorObject as ApiError)?.error

    if (typeof error === 'string') return error.trim()

    return (error?.message ?? '').trim()
}

/*
 * parseError turns an API error body into a sentence.
 *
 * The web translates by CODE, never by message (docs/API.md → Errors): the server's messages
 * are English and meant for a log. A code this console has no sentence for gets the caller's
 * fallback, which names what was being attempted.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (errorCode(errorObject)) {
        case Errors.InvalidData: {
            // The one field to blame, when the API names it (`details.field`).
            const field = errorDetails(errorObject).field

            return typeof field === 'string' && field ? t('errors.invalid_field', { field }) : t('errors.invalid_data')
        }
        case Errors.NotFound:        return t('errors.not_found')
        case Errors.Unauthorized:    return t('errors.unauthorized')
        case Errors.Forbidden:       return t('errors.forbidden')
        case Errors.TooManyAttempts: return t('auth.too_many_attempts')
        case Errors.TenantInactive:  return t('errors.tenant_inactive')
        default:                     return fallback || t('errors.generic_error')
    }
}

/*
 * parseKnownError is parseError plus the refusals a person can actually cause — a taken
 * username or slug, somebody already a member, the last administrator, a short or wrong
 * password, a screen that is gone, a file that is too big or of the wrong kind, something still
 * in use, a PMS that is not set up or did not answer, an EPG source that did not answer, a
 * channel that already has EPG, an APK that is not ours, not signed by the release key, or not
 * newer than the uploaded ones, a theme key already taken (38), a TMS that is not set up or did
 * not answer (92, 93 — its message is the TMS's code and short reason) or a device without a MAC (94). "In use" (17) keeps the server's message: it names what uses the
 * thing. So do the PMS (23) and EPG (29) failures: their message is the remote side's status and
 * short reason (never a token), which is what support asks for — and an unreadable APK (33),
 * whose message names what is missing in it.
 */
export const parseKnownError = (errorObject: unknown, fallback = ''): string => {
    const t       = i18n.global.t
    const reason  = errorMessage(errorObject)
    const details = errorDetails(errorObject)

    switch (errorCode(errorObject)) {
        case Errors.UsernameTaken:          return t('user.username_taken')
        case Errors.LastAdmin:              return t('user.last_admin')
        case Errors.PasswordTooShort:       return t('user.password_too_short')
        case Errors.WrongPassword:          return t('password.wrong_current')
        case Errors.PasskeysUnavailable:    return t('passkey.unavailable')
        case Errors.SlugTaken:              return t('tenant.slug_taken')
        case Errors.EnrolmentUnknown:       return t('pending.gone')
        case Errors.FileTooLarge:           return t('asset.too_large')
        case Errors.UnsupportedMedia:       return t('asset.unsupported')
        case Errors.WeatherUnavailable:     return t('errors.weather_unavailable')
        case Errors.AlreadyMember:          return t('user.already_member')
        case Errors.NotAvailable:           return t('errors.not_available')
        case Errors.ConnectorNotConfigured: return t('errors.connector_not_configured')
        case Errors.ConnectorFailed:        return reason ? t('errors.connector_failed_reason', { reason }) : t('errors.connector_failed')
        case Errors.EpgFetchFailed:         return reason ? t('errors.epg_failed_reason', { reason }) : t('errors.epg_failed')
        case Errors.ChannelAlreadyMapped:   return t('epg.already_mapped')
        case Errors.VersionExists:          return t('apk.version_exists')
        case Errors.ApkInvalid:             return reason ? t('apk.invalid_reason', { reason }) : t('apk.invalid')
        case Errors.ApkWrongPackage:        return t('apk.wrong_package', { name: String(details.package ?? '') })
        case Errors.ApkUnsigned:            return t('apk.unsigned')
        case Errors.ApkWrongSigner:         return t('apk.wrong_signer', { signer: shortFingerprint(String(details.signer_sha256 ?? '')) })
        case Errors.VersionTooLow:          return t('apk.version_too_low', {
            code:    Number(details.version_code ?? 0),
            highest: Number(details.highest ?? 0),
            variant: apkVariantLabel(String(details.variant ?? ApkVariant.Standard)),
        })
        case Errors.ThemeKeyTaken:          return t('screen_theme.key_taken', { key: String(details.key ?? '') })
        case Errors.CommandUnavailable:     return details.reason === 'unpaired'
            ? t('monitoring.blocked_unpaired')
            : t('monitoring.blocked_cap', { cap: String(details.capability ?? '') })
        case Errors.ImportInvalid:          return t('import.invalid')
        case Errors.InUse:                  return reason ? t('errors.in_use_list', { list: reason }) : t('errors.in_use')
        case Errors.TooManyOpenRequests:    return t('errors.too_many_open', { max: Number(details.max ?? 0) })
        case Errors.RequestStatus:          return t('errors.request_status', { status: t(`request_status.${String(details.status ?? '')}`) })
        case Errors.PosFailed:              return reason ? t('errors.pos_failed_reason', { reason }) : t('errors.pos_failed')
        case Errors.PosNotConfigured:       return t('errors.pos_not_configured')
        case Errors.ItemUnavailable:        return t('errors.item_unavailable')
        case Errors.StayOverlap:            return t('errors.stay_overlap', {
            arrival:   formatDay(String(details.arrival ?? '')),
            departure: formatDay(String(details.departure ?? '')),
        })
        case Errors.StayState:              return t('errors.stay_state', { state: t(`stay_state.${String(details.state ?? '')}`) })
        case Errors.StayDates:              return t('errors.stay_dates')
        case Errors.RoomHasStays:           return t('errors.room_has_stays', Number(details.count ?? 0))
        case Errors.TmsNotConfigured:       return t('errors.tms_not_configured')
        case Errors.TmsFailed:              return reason ? t('errors.tms_failed_reason', { reason }) : t('errors.tms_failed')
        case Errors.NoDeviceMac:            return t('errors.no_device_mac')
        default:                            return parseError(errorObject, fallback)
    }
}
